/**
 * TravelSharedTripPage — public, unauthenticated read-only view of a
 * shared itinerary. Renders the List and Calendar tabs from the same
 * components the owner uses, with `readOnly` set so clicks don't try to
 * navigate to editors the viewer can't reach.
 *
 * 404 / expired / revoked states show a friendly explanation + a CTA to
 * "Plan your own trip on Fias" rather than surfacing API errors.
 */

import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, List, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { useSharedTravelTrip } from '../hooks/useTravelShareLinks';
import { TravelListView } from '../components/TravelListView';
import { TravelCalendarView } from '../components/TravelCalendarView';
import type {
  TravelItemResponse,
  TravelEventResponse,
  TravelTripResponse,
  TravelAttachmentResponse,
} from '../api';

type ViewMode = 'list' | 'calendar';

/** CTA shown on 404 and in the footer of successful views. */
function PlanYourOwnCta() {
  return (
    <div className="mt-6 flex justify-center">
      <Link to="/">
        <Button variant="default">
          <Share2 className="h-4 w-4 mr-2" /> Plan your own trip on Fias
        </Button>
      </Link>
    </div>
  );
}

export function TravelSharedTripPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const { data, isLoading, error } = useSharedTravelTrip(shareId);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Widen the sanitized server payload into the shapes the existing views
  // expect. The server strips ownerId + createdAt/updatedAt from items and
  // events; we fill them with a synthetic value so the view components can
  // render without changes.
  const { trip, items, events, attachments } = useMemo(() => {
    if (!data) return { trip: null, items: [], events: [], attachments: [] };
    const FILLER_ISO = new Date(0).toISOString();
    const fullTrip: TravelTripResponse = {
      tripId: data.trip.tripId,
      ownerId: '', // intentionally blank on the public page
      title: data.trip.title,
      startDate: data.trip.startDate,
      status: data.trip.status,
      createdAt: FILLER_ISO,
      updatedAt: FILLER_ISO,
    };
    const fullItems: TravelItemResponse[] = data.items.map((i) => ({
      ...i,
      createdAt: FILLER_ISO,
      updatedAt: FILLER_ISO,
    }));
    const fullEvents: TravelEventResponse[] = data.events.map((e) => ({
      ...e,
      createdAt: FILLER_ISO,
      updatedAt: FILLER_ISO,
    }));
    const fullAttachments: TravelAttachmentResponse[] = data.attachments.map((a) => ({
      attachmentId: a.attachmentId,
      tripId: a.tripId,
      itemId: a.itemId,
      eventId: a.eventId,
      fileId: a.fileId,
      label: a.label,
      displayOrder: a.displayOrder,
      fileName: a.fileName,
      fileSize: a.fileSize,
      mimeType: a.mimeType,
      createdAt: FILLER_ISO,
    }));
    return {
      trip: fullTrip,
      items: fullItems,
      events: fullEvents,
      attachments: fullAttachments,
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <div className="border-b border-border p-4">
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="p-6 max-w-5xl mx-auto w-full space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="flex flex-col min-h-screen bg-background items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="py-6 space-y-3 text-center">
            <h1 className="text-lg font-semibold">Itinerary unavailable</h1>
            <p className="text-sm text-muted-foreground">
              This share link may have expired or been revoked by its owner. Ask the person who sent
              it for a fresh link.
            </p>
            <PlanYourOwnCta />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <header className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto w-full px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">{trip.title}</h1>
            <p className="text-xs text-muted-foreground">
              Starts {trip.startDate} · {items.filter((i) => i.itemType === 'day').length} day
              {items.filter((i) => i.itemType === 'day').length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="flex border border-border rounded-md">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-r-none"
              onClick={() => setViewMode('list')}
            >
              <List className="h-3 w-3 mr-1" /> List
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-l-none"
              onClick={() => setViewMode('calendar')}
            >
              <Calendar className="h-3 w-3 mr-1" /> Calendar
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto w-full p-4">
          {viewMode === 'list' ? (
            <TravelListView
              trip={trip}
              items={items}
              events={events}
              attachments={attachments}
              readOnly
            />
          ) : (
            <TravelCalendarView
              trip={trip}
              items={items}
              events={events}
              attachments={attachments}
              readOnly
            />
          )}
          <PlanYourOwnCta />
        </div>
      </main>

      <footer className="flex-shrink-0 border-t border-border p-3 text-xs text-muted-foreground text-center">
        Shared read-only via Fias. Owners can revoke this link at any time.
      </footer>
    </div>
  );
}
